import {Component} from 'preact';
import {Query} from 'react-apollo';
import {GetDistributorsBySite} from '@services/ResourcesService/query.graphql';
import {Processing} from '@ultradent/components';
import {client} from '@core/GraphQLProvider';
import compose from 'ramda/src/compose';
import map from 'ramda/src/map';
import prop from 'ramda/src/prop';
import propOr from 'ramda/src/propOr';
import uniq from 'ramda/src/uniq';
import flatten from 'ramda/src/flatten';
import cx from 'classnames';

/** DEPRECATED
 *  DEPRECATED This implementation has been replaced with a new design and should be dismantled
 *  /resources/distributors
 */

const FILTER_LABEL_TERRITORY = upi.i18n.getTextFromKey( 'lbl-filterByTerritory', 'Filter by territory' );
const FILTER_OPTION_ALL_TERRITORY = upi.i18n.getTextFromKey( 'lbl-allTerritories', 'All territories' );
const FILTER_LABEL_COUNTRY = upi.i18n.getTextFromKey( 'lbl-filterByCountry', 'Filter by country' );
const FILTER_OPTION_ALL_COUNTRY = upi.i18n.getTextFromKey( 'lbl-allCountries', 'All countries' );
const PHONE_LABEL = upi.i18n.getTextFromKey( 'lbl-phoneWithColon', 'Phone:' );
const FAX_LABEL = upi.i18n.getTextFromKey( 'lbl-faxWithColon', 'Fax:' );
const ERROR_HEADING = upi.i18n.getTextFromKey( 'lbl-errorDistributorListHeading',
    'Sorry, there was problem getting the list of distributors.' );
const ERROR_PHRASE = upi.i18n.getTextFromKey( 'lbl-errorDistributorListPhrase' )
    .replace( '%PHONE_TOLL_FREE%', upi.conf.phoneTollFree );
const LIST_EMPTY_LABEL = upi.i18n.getTextFromKey( 'lbl-distributorListEmptyHeading',
    'Sorry, we were not able to find any distributors for this region.' );
const LIST_EMPTY_PHRASE = upi.i18n.getTextFromKey( 'lbl-distributorListEmptyPhrase',
        'Please try selecting another region or for more information contact our customer service at %PHONE_TOLL_FREE%' )
    .replace( '%PHONE_TOLL_FREE%', upi.conf.phoneTollFree );

const getTerritories = map( propOr( [], 'territory' ) );
const getCountries = map( prop( 'country' ) );
const getFlatTerritories = compose( uniq, flatten, getTerritories );
const getFlatCountries = compose( uniq, getCountries );
const FILTER_THRESHOLD = 10;
const DistributorListEmpty = () => (
    <div>
        <h2 className="text-lg font-normal">{LIST_EMPTY_LABEL}</h2>
        <p className="mt-2 max-w-lg">{LIST_EMPTY_PHRASE}</p>
    </div>
);

const DistributorListError = () => (
    <div className="leading-normal">
        <h2 className="text-lg font-normal color-error">{ERROR_HEADING}</h2>
        <p className="mt-2 max-w-lg">{ERROR_PHRASE}</p>
    </div>
);

const DistributorList = ( {list = []} ) => {
    return (
        <div>
            {list.map( item =>
                <address className="mt-8" key={item.sysId}>
                    {item.logo &&
                        <a className="w-24 h-24 flex items-center md:float-right md:ml-4"
                           target="_blank"
                           href={item.url}>
                        <picture>
                            <source type="image/webp" data-srcset={`${item.logo.source}?h=96&fm=webp`}/>
                            <source data-srcset={`${item.logo.source}?h=96`}/>
                            <img
                                className="lazyload w-auto fade-up"
                                data-src={`${item.logo.source}?h=96`}
                                alt={item.title}/>
                        </picture>

                    </a>
                    }
                    <div>
                        <span className={cx( 'text-xs font-bold uppercase', {'slash-forward': !!item.territory} )}>
                            {item.country}
                        </span>
                        <span className="text-xs font-bold uppercase">{item.territory}</span>
                    </div>
                    <span className="block">
                        <span className="inline-block mb-1 text-lg">{item.title}</span>
                        {item.email &&
                            <a className="link-2 block md:inline-block md:mx-2"
                               href={`mailto:${item.email}`}>{item.email}</a>
                        }
                        {item.url &&
                            <a className="link-2 block md:inline-block md:mx-2" href={item.url}
                               target="_blank">{item.url}</a>
                        }
                    </span>
                    <span className="block mt-2" dangerouslySetInnerHTML={{__html: item.address}}/>
                    {item.fax && <span className="block mt-1">{FAX_LABEL} {item.fax}</span>}
                    {item.tollFree && <span className="block">{PHONE_LABEL} {item.tollFree}</span>}
                </address>
            )}
        </div>
    );
};

export class Distributors extends Component {
    constructor ( props ) {
        super( props );

        this.onTerritoryChange = this.onTerritoryChange.bind( this );
        this.onCountryChange = this.onCountryChange.bind( this );
        this.state = {
            currentCountry: 'all',
            currentTerritory: 'all'
        };
    }

    onCountryChange ( e ) {
        const currentCountry = e.target.value;
        this.setState( {
            currentCountry,
            currentTerritory: 'all'
        } );
    }

    onTerritoryChange ( e ) {
        const currentTerritory = e.target.value;
        this.setState( {currentTerritory} );
    }

    shouldFilterByCountry = ( countries, list ) => {
        return countries.length > 1 && list.length > FILTER_THRESHOLD;
    };

    shouldFilterByTerritory = ( countries, territories, list ) => {
        return territories.length > 1
            && list.length > FILTER_THRESHOLD
            && (this.state.currentCountry !== 'all' || countries.length === 1);
    };

    render () {
        const {currentCountry, currentTerritory} = this.state;
        return (
            <Query
                client={client}
                query={GetDistributorsBySite}>
                {( {data, error, loading} ) => {
                    if ( loading ) {
                        return <Processing/>
                    }

                    if ( error || !data.distributorBySite ) {
                        return <DistributorListError/>
                    }

                    let filteredList = data.distributorBySite;

                    // first filter by country if applicable (if only one country ignore country filter)
                    const countries = getFlatCountries( filteredList );
                    let showCountryFilter = this.shouldFilterByCountry( countries, filteredList );
                    if ( showCountryFilter ) {
                        filteredList = (currentCountry === 'all')
                            ? filteredList
                            : filteredList.filter( ( {country} ) => country === currentCountry )
                    }
                    // territory filter is dependent on country first, filter territories based on current country filter
                    const territories = getFlatTerritories( filteredList );
                    let showTerritoryFilter = this.shouldFilterByTerritory( countries, territories, filteredList );
                    if ( showTerritoryFilter ) {
                        filteredList = (currentTerritory === 'all')
                            ?
                            filteredList
                            :
                            filteredList.filter( ( {territory} ) => territory &&
                                territory.includes( currentTerritory ) );
                    }

                    if ( !filteredList.length ) {
                        return <DistributorListEmpty/>
                    }

                    return (
                        <div className="md:flex">
                            {(showTerritoryFilter || showCountryFilter) &&
                                <div className="flex-0 md:mr-16">
                                {showCountryFilter &&
                                    <div className="mb-4">
                                    <label className="mb-2 text-lg" htmlFor="countryFilter">
                                        {FILTER_LABEL_COUNTRY}
                                    </label>
                                    <select name="countryFilter" id="countryFilter"
                                            className="w-full"
                                            onChange={this.onCountryChange}>
                                        {countries.map( ( val, i ) =>
                                            <option
                                                key={`${val}:${i}`}
                                                selected={currentCountry === val}
                                                value={val}>
                                                {val}
                                            </option>
                                        )}
                                        <option value="all" selected={currentCountry === 'all'}>
                                            {FILTER_OPTION_ALL_COUNTRY}
                                        </option>
                                    </select>
                                </div>
                                }
                                    {showTerritoryFilter &&
                                        <div className="mb-4">
                                    <label className="mb-2 text-lg" htmlFor="territoryFilter">
                                        {FILTER_LABEL_TERRITORY}
                                    </label>
                                    <select name="territoryFilter" id="territoryFilter"
                                            className="w-full"
                                            onChange={this.onTerritoryChange}>
                                        {territories.map( ( val, i ) =>
                                            <option
                                                key={`${val}:${i}`}
                                                selected={currentTerritory === val}
                                                value={val}>
                                                {val}
                                            </option>
                                        )}
                                        <option value="all" selected={currentTerritory === 'all'}>
                                            {FILTER_OPTION_ALL_TERRITORY}
                                        </option>
                                    </select>
                                </div>
                                    }
                            </div>
                            }
                            <DistributorList list={filteredList}/>
                        </div>
                    )
                }}
            </Query>
        );
    }
}